import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import { AddUserDialog } from "./AddUserDialog";
import { UserList } from "./UserList";
import { toast } from "sonner";

export const UserManagementTab = () => {
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);

  const { data: users, isLoading, refetch } = useQuery({
    queryKey: ['owner-users'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        console.log("Current user:", user.id);

        // First get owner_users
        const { data: ownerUsers, error: ownerUsersError } = await supabase
          .from('owner_users')
          .select('*')
          .eq('owner_id', user.id);

        if (ownerUsersError) {
          console.error('Error fetching owner users:', ownerUsersError);
          throw ownerUsersError;
        }

        console.log("Owner users:", ownerUsers);

        if (!ownerUsers?.length) return [];

        // Then get the profile information for each user
        const userIds = ownerUsers.map(ou => ou.user_id);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url')
          .in('id', userIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }

        console.log("Profiles:", profiles);

        // Combine the data
        return ownerUsers.map(ou => ({
          ...ou,
          profiles: profiles?.find(p => p.id === ou.user_id) || null
        }));

      } catch (error) {
        console.error('Error in query:', error);
        toast.error("Erreur lors du chargement des utilisateurs");
        throw error;
      }
    },
    retry: 1
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold tracking-tight">Gestion des utilisateurs</h2>
        <Button onClick={() => setIsAddUserDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Ajouter un utilisateur
        </Button>
      </div>

      <Card className="p-6">
        <UserList 
          users={users || []} 
          isLoading={isLoading}
          onUserDeleted={refetch}
        />
      </Card>

      <AddUserDialog 
        open={isAddUserDialogOpen} 
        onOpenChange={setIsAddUserDialogOpen}
        onSuccess={() => {
          refetch();
          setIsAddUserDialogOpen(false);
        }}
      />
    </div>
  );
};